/**
 * @fileoverview Multi unidades — painel operacional multi-unidade.
 *
 * Estrutura inspirada na proposta Zenvia, adaptada para o dark mode premium
 * do Sttelar. 4 views internas:
 *   - overview (default): KPIs + tabela Performance por Smart Center
 *   - charts: gráficos operacionais histórico (8 séries)
 *   - sms: módulo SMS Alerts (config + partners)
 *   - unit: drill-in de casos abertos de uma unidade específica
 *
 * Dados via ./mockData. Cada endpoint mockado tem comentário com a
 * sugestão de rota REST quando o backend implementar.
 */

import React, { useMemo, useState, useEffect, useCallback, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import MultiUnidadesV2 from "./v2/MultiUnidadesV2";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Dialog,
  Snackbar,
} from "@material-ui/core";
import {
  Activity,
  AlertCircle,
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  BellRing,
  Building,
  Calendar,
  ChartLine,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  Clock,
  Download,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  Filter,
  Folder,
  FolderOpen,
  Hourglass,
  Info,
  LineChart as LineChartIcon,
  MessageSquare,
  RefreshCw,
  Search,
  Table as TableIcon,
  Timer,
  User,
  Users,
  X,
} from "lucide-react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";

import ModernSelect from "../../components/ModernSelect";
import { exportableReports, statusOptions, periodOptions } from "./mockData";
import { useMultiUnidadesData } from "./data/useMultiUnidadesData";
import {
  loadSmsConfig,
  saveSmsSetting,
  savePhones as savePhonesList,
  setEnabled as setSmsEnabled,
  sendTest as sendTestSms,
} from "./data/smsService";
import "./data/validate"; // expõe window.__validateMultiUnidades
import SmsAlertsView from "./SmsAlertsView";
import { DashboardGeralBody } from "../DashboardGeral";

// ── Tokens ────────────────────────────────────────────────────────────
const getThemeTokens = (theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    isDark,
    pageBg: isDark ? "#0A0A0A" : "#F7F8FA",
    cardBg: isDark ? "rgba(14,16,22,0.85)" : "#ffffff",
    cardBgStrong: isDark ? "rgba(18,20,28,0.96)" : "#ffffff",
    cardBgInner: isDark ? "rgba(255,255,255,0.025)" : "rgba(15,23,42,0.025)",
    cardBorder: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.10)",
    cardBorderStrong: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.16)",
    hoverBorder: isDark ? "rgba(124,154,255,0.30)" : "rgba(59,130,246,0.26)",
    hoverShadow: isDark
      ? "0 1px 0 rgba(124,154,255,0.08) inset, 0 8px 24px rgba(0,0,0,0.36)"
      : "0 8px 24px rgba(15,23,42,0.08)",
    textPrimary: isDark ? "#F2F4F8" : "#0F172A",
    textSecondary: isDark ? "#9CA3AF" : "#475569",
    textMuted: isDark ? "#6B7280" : "#64748B",
    textSubtle: isDark ? "#4B5563" : "#94A3B8",
    accent: isDark ? "#7C9AFF" : "#3D5CFD",
    accentStrong: isDark ? "#5B7CFF" : "#1E40FF",
    accentSoft: isDark ? "rgba(124,154,255,0.14)" : "rgba(61,92,253,0.10)",
    accentSoftStrong: isDark ? "rgba(124,154,255,0.22)" : "rgba(61,92,253,0.16)",
    success: isDark ? "#34D399" : "#059669",
    successSoft: isDark ? "rgba(52,211,153,0.12)" : "rgba(5,150,105,0.10)",
    warning: isDark ? "#FBBF24" : "#D97706",
    warningSoft: isDark ? "rgba(251,191,36,0.12)" : "rgba(217,119,6,0.10)",
    danger: isDark ? "#F87171" : "#DC2626",
    dangerSoft: isDark ? "rgba(248,113,113,0.12)" : "rgba(220,38,38,0.10)",
    chartGrid: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.06)",
    rowHover: isDark ? "rgba(255,255,255,0.025)" : "rgba(59,130,246,0.04)",
    inputBg: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
  };
};

const useStyles = makeStyles((theme) => {
  const c = getThemeTokens(theme);
  return {
    // ── Root scrollable ────────────────────────────────────────────────
    // .content do LoggedInLayout: display:flex; flex-direction:column;
    // overflow:hidden; antes do children renderiza <ImpersonationBanner/>.
    // Pra rolar sem cobrir o banner: flex:1 + min-height:0 (libera o flex
    // child a shrink) + overflowY:auto = scroll interno funcionando.
    pageWrap: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      backgroundColor: c.pageBg,
      backgroundImage: c.isDark
        ? `radial-gradient(circle at 20% -10%, rgba(124,154,255,0.06), transparent 45%),
           radial-gradient(circle at 80% 0%, rgba(124,154,255,0.04), transparent 40%)`
        : "none",
      fontFamily: `"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif`,
      color: c.textPrimary,
      "&::-webkit-scrollbar": { width: 8 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: c.isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.16)",
        borderRadius: 4,
      },
    },
    page: {
      width: "100%",
      maxWidth: 1600,
      margin: "0 auto",
      padding: "20px 24px 60px",
      [theme.breakpoints.down("sm")]: { padding: "14px 12px 80px" },
    },

    // ── Header ─────────────────────────────────────────────────────────
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    headerLeft: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    titleRow: { display: "flex", alignItems: "center", gap: 12 },
    titlePillIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      background: `linear-gradient(135deg, ${c.accentSoftStrong}, ${c.accentSoft})`,
      color: c.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: c.isDark
        ? `inset 0 0 0 1px rgba(124,154,255,0.18)`
        : `inset 0 0 0 1px rgba(61,92,253,0.18)`,
    },
    titleStack: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    title: {
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      lineHeight: 1.1,
    },
    subtitle: {
      fontSize: 12.5,
      color: c.textSecondary,
      lineHeight: 1.4,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    headerMeta: {
      fontSize: 11,
      color: c.textSubtle,
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginRight: 4,
      whiteSpace: "nowrap",
    },

    // ── Tab switcher (Visão geral / Gráficos / SMS) ────────────────────
    tabBar: {
      display: "inline-flex",
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: 10,
      padding: 4,
      gap: 2,
    },
    tabBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "7px 12px",
      borderRadius: 6,
      border: "none",
      background: "transparent",
      color: c.textSecondary,
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { color: c.textPrimary },
    },
    tabBtnActive: {
      background: c.accentSoftStrong,
      color: c.accent,
    },

    // ── Filtros ────────────────────────────────────────────────────────
    filtersRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      marginBottom: 16,
      flexWrap: "wrap",
    },
    filterField: {
      minWidth: 140,
      // Em telas <600px, o select preenche toda a largura disponível em vez
      // de ancorar em minWidth fixo — evita fileira horizontal apertada no mobile.
      "@media (max-width: 600px)": {
        width: "100%",
        minWidth: 0,
      },
      "& .MuiOutlinedInput-root": {
        background: c.inputBg,
        borderRadius: 8,
        fontSize: 12.5,
        height: 36,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: c.cardBorder },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: c.cardBorderStrong,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: c.accent,
      },
      "& .MuiInputLabel-root": {
        fontSize: 11,
        color: c.textMuted,
        transform: "translate(12px, 11px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(12px, -6px) scale(0.85)",
          background: c.cardBg,
          padding: "0 4px",
        },
      },
      "& .MuiSelect-icon": { color: c.textMuted },
      "& .MuiSelect-select": { color: c.textPrimary },
    },
    iconBtn: {
      width: 36,
      height: 36,
      borderRadius: 8,
      border: `1px solid ${c.cardBorder}`,
      background: c.inputBg,
      color: c.textSecondary,
      "&:hover": {
        background: c.cardBgStrong,
        color: c.accent,
        borderColor: c.hoverBorder,
      },
    },
    primaryBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      borderRadius: 8,
      border: `1px solid ${c.accent}`,
      background: c.accent,
      color: "#fff",
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { filter: "brightness(0.96)", transform: "translateY(-1px)" },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed", transform: "none" },
    },
    ghostBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      borderRadius: 8,
      border: `1px solid ${c.cardBorder}`,
      background: "transparent",
      color: c.textSecondary,
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { background: c.accentSoft, color: c.accent, borderColor: c.hoverBorder },
    },

    // ── KPI grid ───────────────────────────────────────────────────────
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 10,
      marginBottom: 16,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, 1fr)" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    kpiCard: {
      position: "relative",
      padding: "14px 16px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      gap: 6,
      minHeight: 124,
      transition: "all 0.15s ease",
      overflow: "hidden",
      "&:hover": {
        borderColor: c.hoverBorder,
        boxShadow: c.hoverShadow,
        transform: "translateY(-1px)",
      },
    },
    kpiCardAccent: {
      position: "absolute",
      top: 0, left: 0, bottom: 0,
      width: 3,
    },
    kpiTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    kpiLabel: {
      fontSize: 11.5,
      color: c.textSecondary,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      lineHeight: 1.2,
      flex: 1,
    },
    kpiIconWrap: {
      width: 28,
      height: 28,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    kpiValue: {
      fontSize: 26,
      fontWeight: 700,
      color: c.textPrimary,
      lineHeight: 1.05,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.01em",
    },
    kpiDesc: { fontSize: 11, color: c.textMuted, fontWeight: 500 },
    kpiFooter: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      marginTop: "auto",
    },
    deltaUp: { color: c.success, display: "inline-flex", alignItems: "center", gap: 3 },
    deltaDown: { color: c.danger, display: "inline-flex", alignItems: "center", gap: 3 },
    deltaNeutral: { color: c.textMuted, display: "inline-flex", alignItems: "center", gap: 3 },
    deltaNote: { color: c.textMuted, marginLeft: 4, fontWeight: 500 },
    kpiInfoBtn: {
      width: 20,
      height: 20,
      padding: 0,
      color: c.textSubtle,
      "&:hover": { color: c.accent, background: "transparent" },
    },

    // ── Section / card container ───────────────────────────────────────
    section: {
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      overflow: "hidden",
      marginBottom: 16,
    },
    sectionHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "14px 16px",
      borderBottom: `1px solid ${c.cardBorder}`,
      flexWrap: "wrap",
    },
    sectionTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13.5,
      fontWeight: 700,
      color: c.textPrimary,
    },
    sectionTools: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
    countPill: {
      fontSize: 11,
      fontWeight: 600,
      color: c.accent,
      padding: "2px 8px",
      borderRadius: 999,
      background: c.accentSoft,
      marginLeft: 4,
    },
    searchField: {
      width: 240,
      "& .MuiOutlinedInput-root": {
        background: c.inputBg,
        borderRadius: 8,
        fontSize: 12.5,
        height: 34,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: c.cardBorder },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: c.accent,
      },
      "& input": { padding: "8px 0", color: c.textPrimary },
      "& input::placeholder": { color: c.textSubtle, opacity: 1 },
    },

    // ── Tabela ─────────────────────────────────────────────────────────
    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
    th: {
      textAlign: "left",
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: c.textMuted,
      padding: "12px 14px",
      borderBottom: `1px solid ${c.cardBorder}`,
      background: c.isDark ? "rgba(255,255,255,0.015)" : "rgba(15,23,42,0.015)",
      whiteSpace: "nowrap",
      userSelect: "none",
    },
    thSortable: {
      cursor: "pointer",
      "&:hover": { color: c.textPrimary },
    },
    thSortActive: { color: c.accent },
    thSortIcon: {
      display: "inline-flex",
      verticalAlign: "middle",
      marginLeft: 4,
      opacity: 0.6,
      transition: "transform 0.2s ease",
    },
    tr: {
      borderBottom: `1px solid ${c.cardBorder}`,
      transition: "background 0.12s ease",
      "&:hover": { background: c.rowHover },
      "&:last-child": { borderBottom: "none" },
    },
    td: {
      padding: "12px 14px",
      color: c.textPrimary,
      fontVariantNumeric: "tabular-nums",
      verticalAlign: "middle",
    },
    tdName: { fontWeight: 600 },
    tdMuted: { color: c.textSecondary },
    tdNum: { textAlign: "right" },
    statusDot: {
      display: "inline-block",
      width: 9,
      height: 9,
      borderRadius: "50%",
    },
    statusOk: { background: c.success, boxShadow: `0 0 0 4px ${c.successSoft}` },
    statusWarn: { background: c.warning, boxShadow: `0 0 0 4px ${c.warningSoft}` },
    statusFail: { background: c.danger, boxShadow: `0 0 0 4px ${c.dangerSoft}` },
    timeBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
    timeBadgeOk: { background: c.successSoft, color: c.success, border: `1px solid ${c.successSoft}` },
    timeBadgeFail: { background: c.dangerSoft, color: c.danger, border: `1px solid ${c.dangerSoft}` },
    actionsCell: { display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" },
    actionBtn: {
      width: 30,
      height: 30,
      color: c.textSecondary,
      "&:hover": { color: c.accent, background: c.accentSoft },
    },

    // ── Pagination ────────────────────────────────────────────────────
    pagination: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 14px",
      borderTop: `1px solid ${c.cardBorder}`,
      gap: 12,
      flexWrap: "wrap",
    },
    pageInfo: { fontSize: 11.5, color: c.textMuted },
    pageNav: { display: "flex", alignItems: "center", gap: 4 },
    pageBtn: {
      width: 30,
      height: 30,
      color: c.textSecondary,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: 6,
      background: "transparent",
      "&:hover": { color: c.accent, borderColor: c.hoverBorder, background: c.accentSoft },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    },
    pageSelect: {
      fontSize: 12,
      color: c.textSecondary,
      "& select": {
        appearance: "none",
        background: c.inputBg,
        color: c.textPrimary,
        border: `1px solid ${c.cardBorder}`,
        borderRadius: 6,
        padding: "4px 22px 4px 8px",
        fontSize: 12,
        cursor: "pointer",
      },
    },

    // ── Legend ─────────────────────────────────────────────────────────
    legendRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      borderTop: `1px solid ${c.cardBorder}`,
      gap: 12,
      flexWrap: "wrap",
    },
    legend: { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" },
    legendItem: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: c.textSecondary },
    legendNote: { fontSize: 11, color: c.textSubtle, display: "inline-flex", alignItems: "center", gap: 5 },

    // ── Drill-in (Unit detail) ────────────────────────────────────────
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 12px",
      borderRadius: 8,
      border: `1px solid ${c.cardBorder}`,
      background: "transparent",
      color: c.textSecondary,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      marginBottom: 12,
      transition: "all 0.15s ease",
      "&:hover": { borderColor: c.hoverBorder, color: c.accent },
    },
    detailHeader: {
      padding: "18px 20px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      marginBottom: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      flexWrap: "wrap",
    },
    detailHeaderLeft: { display: "flex", flexDirection: "column", gap: 4 },
    detailHeaderName: {
      fontSize: 18,
      fontWeight: 700,
      color: c.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    detailHeaderSub: { fontSize: 12, color: c.textSecondary },
    ascPill: {
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 9px",
      borderRadius: 999,
      background: c.accentSoftStrong,
      color: c.accent,
    },

    miniGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 10,
      marginBottom: 14,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, 1fr)" },
    },
    miniCard: {
      position: "relative",
      padding: "12px 14px",
      borderRadius: 10,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      transition: "all 0.15s ease",
      "&:hover": { borderColor: c.hoverBorder },
    },
    miniLabel: {
      fontSize: 10.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: c.textMuted,
    },
    miniValue: {
      fontSize: 20,
      fontWeight: 700,
      color: c.textPrimary,
      fontVariantNumeric: "tabular-nums",
      marginTop: 4,
    },
    miniDelta: { fontSize: 10.5, marginTop: 2 },

    // ── Charts ─────────────────────────────────────────────────────────
    chartsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 12,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, 1fr)" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    sectionDivider: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      margin: "12px 0",
      "&::before, &::after": {
        content: "''",
        flex: 1,
        height: 1,
        background: c.cardBorderSoft,
      },
    },
    sectionDividerLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      color: c.textMuted,
    },
    chartCard: {
      padding: "14px 14px 8px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minHeight: 220,
      transition: "all 0.15s ease",
      "&:hover": { borderColor: c.hoverBorder },
    },
    chartTitleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    chartTitle: {
      fontSize: 12,
      fontWeight: 700,
      color: c.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    chartLastValue: {
      fontSize: 18,
      fontWeight: 700,
      color: c.textPrimary,
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.05,
    },
    chartSub: { fontSize: 10.5, color: c.textMuted, marginBottom: 2 },

    // ── Export Modal ───────────────────────────────────────────────────
    exportDialog: {
      "& .MuiDialog-paper": {
        borderRadius: 16,
        background: c.cardBgStrong,
        border: `1px solid ${c.cardBorder}`,
        maxWidth: 680,
        width: "100%",
        color: c.textPrimary,
        overflow: "hidden",
      },
    },
    exportHeader: {
      padding: "16px 20px",
      borderBottom: `1px solid ${c.cardBorder}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    exportTitle: { fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 },
    exportSubtitle: { fontSize: 12, color: c.textSecondary, marginTop: 2 },
    exportBody: { padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 },
    exportPeriodRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    reportsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 8,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    reportItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "12px 14px",
      borderRadius: 10,
      border: `1px solid ${c.cardBorder}`,
      background: "transparent",
      cursor: "pointer",
      textAlign: "left",
      transition: "all 0.15s ease",
      "&:hover": { background: c.accentSoft, borderColor: c.hoverBorder },
    },
    reportItemActive: { background: c.accentSoftStrong, borderColor: c.accent },
    reportIcon: {
      width: 32,
      height: 32,
      borderRadius: 8,
      background: c.accentSoft,
      color: c.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    reportName: { fontSize: 13, fontWeight: 700, color: c.textPrimary },
    reportDesc: { fontSize: 11.5, color: c.textSecondary, marginTop: 2, lineHeight: 1.4 },
    exportFoot: {
      padding: "12px 20px",
      borderTop: `1px solid ${c.cardBorder}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
    },

    // ── SMS Alerts ─────────────────────────────────────────────────────
    smsConfigCard: {
      padding: "16px 18px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      marginBottom: 16,
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 16,
      alignItems: "center",
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
    },
    smsConfigLeft: { display: "flex", flexDirection: "column", gap: 8 },
    smsConfigTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: c.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    smsConfigDesc: {
      fontSize: 11.5,
      color: c.textSecondary,
      lineHeight: 1.5,
      maxWidth: 460,
    },
    smsLevels: {
      display: "grid",
      gridTemplateColumns: "repeat(5, minmax(72px, 92px))",
      gap: 8,
    },
    smsLevelBox: {
      padding: "10px 8px",
      borderRadius: 10,
      background: c.cardBgInner,
      border: `1px solid ${c.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      transition: "all 0.15s ease",
      "&:hover": { borderColor: c.hoverBorder, background: c.accentSoft },
    },
    smsLevelLabel: {
      fontSize: 10,
      fontWeight: 700,
      color: c.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    },
    smsLevelIcon: {
      width: 26,
      height: 26,
      borderRadius: 8,
      background: c.accentSoft,
      color: c.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    smsLevelMinutes: {
      fontSize: 13,
      fontWeight: 700,
      color: c.textPrimary,
      fontVariantNumeric: "tabular-nums",
    },

    smsCountersCard: {
      padding: "16px 18px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      [theme.breakpoints.down("md")]: { marginTop: 8 },
    },
    smsCountersHead: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 13,
      fontWeight: 700,
      color: c.textPrimary,
    },
    smsCounterIcon: {
      width: 38,
      height: 38,
      borderRadius: 10,
      background: c.accentSoftStrong,
      color: c.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    smsCountersRow: {
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      gap: 14,
    },
    smsCounterBox: { textAlign: "center" },
    smsCounterLevel: {
      fontSize: 11,
      fontWeight: 600,
      color: c.textMuted,
      marginBottom: 2,
    },
    smsCounterValue: {
      fontSize: 20,
      fontWeight: 700,
      color: c.accent,
      fontVariantNumeric: "tabular-nums",
    },

    smsPartnerCard: {
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      marginBottom: 10,
      overflow: "hidden",
    },
    smsPartnerHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      cursor: "pointer",
      transition: "background 0.12s ease",
      "&:hover": { background: c.cardBgInner },
    },
    smsPartnerHeadInfo: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    smsPartnerName: {
      fontSize: 13,
      fontWeight: 700,
      color: c.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    smsPartnerMeta: { fontSize: 11.5, color: c.textMuted },
    smsPartnerBadge: {
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 10px",
      borderRadius: 999,
      background: c.accentSoft,
      color: c.accent,
    },
    smsPartnerChevron: {
      transition: "transform 0.2s ease",
      color: c.textMuted,
    },
    smsLevelChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "3px 9px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },

    smsSummary: {
      padding: "14px 18px",
      borderRadius: 12,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      display: "grid",
      gridTemplateColumns: "auto repeat(4, 1fr)",
      gap: 18,
      alignItems: "center",
      marginTop: 8,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr 1fr" },
    },
    smsSummaryLeft: { display: "flex", alignItems: "center", gap: 10 },
    smsSummaryLabel: {
      fontSize: 10.5,
      fontWeight: 600,
      color: c.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    smsSummaryValue: {
      fontSize: 18,
      fontWeight: 700,
      color: c.textPrimary,
      fontVariantNumeric: "tabular-nums",
      marginTop: 2,
    },

    // Animation
    spin: { animation: "$muSpin 0.8s linear infinite" },
    "@keyframes muSpin": {
      from: { transform: "rotate(0)" },
      to: { transform: "rotate(360deg)" },
    },
  };
});

// ── Helpers ───────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");

// Defensivo: aceita string ou number, retorna "—" se NaN.
const secToHms = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return "—";
  const total = Math.round(n);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const r = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(r)}`;
};

const fmtNumberPtBR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR");
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch (_) {
    return iso;
  }
};

const KPI_ICON_MAP = {
  MessageSquare, Timer, Users, Clock, User, Folder, Hourglass, Activity, ChartLine,
};

const REPORT_ICON_MAP = {
  FileSpreadsheet, FolderOpen, ClipboardList, Timer, User, Hourglass, Activity, ChartLine,
};

// ── KPI Card ──────────────────────────────────────────────────────────
const KpiCard = ({ kpi, colors, classes }) => {
  const Icon = KPI_ICON_MAP[kpi.icon] || Activity;
  const accentColor = (() => {
    switch (kpi.accent) {
      case "rose": return colors.danger;
      case "emerald": return colors.success;
      case "amber": return colors.warning;
      case "indigo":
      default: return colors.accent;
    }
  })();
  const accentSoft = (() => {
    switch (kpi.accent) {
      case "rose": return colors.dangerSoft;
      case "emerald": return colors.successSoft;
      case "amber": return colors.warningSoft;
      case "indigo":
      default: return colors.accentSoft;
    }
  })();

  return (
    <div className={classes.kpiCard}>
      <span className={classes.kpiCardAccent} style={{ background: accentColor }} />
      <div className={classes.kpiTop}>
        <div className={classes.kpiLabel}>{kpi.label}</div>
        <div className={classes.kpiIconWrap} style={{ background: accentSoft, color: accentColor }}>
          <Icon size={16} strokeWidth={2} />
        </div>
      </div>
      <div className={classes.kpiValue}>{kpi.value}</div>
      <div className={classes.kpiDesc}>{kpi.description}</div>
      <div className={classes.kpiFooter}>
        {kpi.delta ? (
          <span className={kpi.delta.negative ? classes.deltaDown : classes.deltaUp}>
            {kpi.delta.direction === "up" ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
            {String(kpi.delta.pct).replace(".", ",")}%
            <span className={classes.deltaNote}>vs. período anterior</span>
          </span>
        ) : (
          <span className={classes.deltaNeutral}>{kpi.note || ""}</span>
        )}
        <span style={{ marginLeft: "auto" }}>
          <Tooltip title={kpi.tooltip} placement="top" arrow>
            <IconButton size="small" className={classes.kpiInfoBtn}>
              <Info size={12} />
            </IconButton>
          </Tooltip>
        </span>
      </div>
    </div>
  );
};

// ── Sortable header cell ──────────────────────────────────────────────
const Th = ({ field, label, sortBy, sortDir, onSort, align, classes, sortable = true, width }) => {
  const active = sortBy === field;
  return (
    <th
      className={`${classes.th} ${sortable ? classes.thSortable : ""} ${active ? classes.thSortActive : ""}`}
      style={{ textAlign: align || "left", width: width || "auto" }}
      onClick={sortable ? () => onSort(field) : undefined}
    >
      {label}
      {active && (
        <span
          className={classes.thSortIcon}
          style={{ transform: sortDir === "asc" ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <ChevronDown size={11} strokeWidth={2.5} />
        </span>
      )}
    </th>
  );
};

// ── Performance Table ─────────────────────────────────────────────────
const PerformanceTable = ({ rows, classes, colors, onOpenUnit, page, pageSize, total, sortBy, sortDir, onSort, onPageChange, onPageSizeChange }) => {
  if (!rows.length) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: colors.textMuted, fontSize: 13 }}>
        Nenhuma unidade encontrada com os filtros atuais.
      </div>
    );
  }

  const StatusDot = ({ status }) => {
    const cls =
      status === "ok" ? classes.statusOk
      : status === "warn" ? classes.statusWarn
      : classes.statusFail;
    return <span className={`${classes.statusDot} ${cls}`} aria-label={status} />;
  };

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <Th field="status" label="Status" width={70} sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="name" label="Smart Center" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="ascCode" label="ASC Code" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="totalOpen" label="Casos abertos" align="right" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="avgFirstReplyAttendant" label="1ª resp. atendente" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="avgFirstReplyClient" label="1ª resp. cliente" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="waitingClient" label="Aguardando cliente" align="right" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="avgInteractionTime" label="Tempo interação" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th field="totalToday" label="Atendimentos" align="right" sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
              <Th label="Ações" align="right" sortable={false} sortBy={sortBy} sortDir={sortDir} onSort={onSort} classes={classes} />
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className={classes.tr}>
                <td className={classes.td}><StatusDot status={u.status} /></td>
                <td className={`${classes.td} ${classes.tdName}`}>{u.name}</td>
                <td className={`${classes.td} ${classes.tdMuted}`}>{u.ascCode}</td>
                <td className={`${classes.td} ${classes.tdNum}`}>{u.totalOpen}</td>
                <td className={classes.td}>
                  <span className={`${classes.timeBadge} ${u.avgFirstReplyAttendantOk ? classes.timeBadgeOk : classes.timeBadgeFail}`}>
                    {u.avgFirstReplyAttendant}
                    {u.avgFirstReplyAttendantOk ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                  </span>
                </td>
                <td className={classes.td}>
                  <span className={`${classes.timeBadge} ${u.avgFirstReplyClientOk ? classes.timeBadgeOk : classes.timeBadgeFail}`}>
                    {u.avgFirstReplyClient}
                    {u.avgFirstReplyClientOk ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                  </span>
                </td>
                <td className={`${classes.td} ${classes.tdNum}`}>{u.waitingClient}</td>
                <td className={classes.td}>{u.avgInteractionTime}</td>
                <td className={`${classes.td} ${classes.tdNum}`}>{fmtNumberPtBR(u.totalToday)}</td>
                <td className={classes.td}>
                  <div className={classes.actionsCell}>
                    <Tooltip title="Ver detalhes" arrow>
                      <IconButton size="small" className={classes.actionBtn} onClick={() => onOpenUnit(u)}>
                        <Eye size={14} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Exportar dados da unidade" arrow>
                      <IconButton size="small" className={classes.actionBtn}>
                        <Download size={14} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className={classes.pagination}>
        <span className={classes.pageInfo}>
          Exibindo <strong style={{ color: colors.textPrimary }}>{start}</strong>–<strong style={{ color: colors.textPrimary }}>{end}</strong> de <strong style={{ color: colors.textPrimary }}>{total}</strong> unidades
        </span>
        <div className={classes.pageNav}>
          <label className={classes.pageSelect}>
            Linhas:&nbsp;
            <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <IconButton size="small" className={classes.pageBtn} disabled={page <= 1} onClick={() => onPageChange(1)}>
            <ChevronsLeft size={14} />
          </IconButton>
          <IconButton size="small" className={classes.pageBtn} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            <ChevronLeft size={14} />
          </IconButton>
          <span style={{ fontSize: 12, color: colors.textPrimary, padding: "0 8px", fontVariantNumeric: "tabular-nums" }}>
            {page} / {totalPages}
          </span>
          <IconButton size="small" className={classes.pageBtn} disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            <ChevronRight size={14} />
          </IconButton>
          <IconButton size="small" className={classes.pageBtn} disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>
            <ChevronsRight size={14} />
          </IconButton>
        </div>
      </div>
    </>
  );
};

// ── Chart card ────────────────────────────────────────────────────────
const ChartCard = ({ title, sub, data, valueFormatter, classes, colors, Icon, last, gradientId }) => (
  <div className={classes.chartCard}>
    <div className={classes.chartTitleRow}>
      <div className={classes.chartTitle}>
        {Icon && <Icon size={14} strokeWidth={1.75} />}
        {title}
      </div>
      <Tooltip title={sub || ""} arrow>
        <IconButton size="small" style={{ color: colors.textSubtle, padding: 2 }}>
          <Info size={12} />
        </IconButton>
      </Tooltip>
    </div>
    <div className={classes.chartLastValue}>{valueFormatter ? valueFormatter(last) : last}</div>
    <div className={classes.chartSub}>último valor · 12 meses</div>
    <div style={{ flex: 1, minHeight: 140 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 6, bottom: 0, left: -16 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.accent} stopOpacity={0.4} />
              <stop offset="100%" stopColor={colors.accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={colors.chartGrid} vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: colors.textSubtle }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: colors.textSubtle }} tickLine={false} axisLine={false} width={36} />
          <RTooltip
            contentStyle={{
              background: colors.cardBgStrong,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: 8,
              fontSize: 12,
              color: colors.textPrimary,
            }}
            labelStyle={{ color: colors.textSecondary }}
            formatter={(v) => [valueFormatter ? valueFormatter(v) : v, ""]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={colors.accent}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            isAnimationActive
            animationDuration={400}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

// ── Export Modal ──────────────────────────────────────────────────────
const ExportModal = ({ open, onClose, classes, colors, onConfirm }) => {
  const [period, setPeriod] = useState("30d");
  const [selected, setSelected] = useState([]);

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleConfirm = () => {
    onConfirm({ period, reports: selected });
    setSelected([]);
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.exportDialog}>
      <div className={classes.exportHeader}>
        <div>
          <div className={classes.exportTitle}>
            <Download size={18} strokeWidth={1.75} />
            Exportar relatórios
          </div>
          <div className={classes.exportSubtitle}>
            Selecione um ou mais relatórios e o período para gerar.
          </div>
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: colors.textMuted }}>
          <X size={18} />
        </IconButton>
      </div>
      <div className={classes.exportBody}>
        <div className={classes.exportPeriodRow}>
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>Período</span>
          <ModernSelect
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className={classes.filterField}
            formControlProps={{ size: "small", style: { minWidth: 200 } }}
          >
            {periodOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </ModernSelect>
          <span style={{ fontSize: 11, color: colors.textSubtle, marginLeft: 8 }}>
            <Calendar size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Dados serão consolidados no período escolhido.
          </span>
        </div>

        <div className={classes.reportsGrid}>
          {exportableReports.map((r) => {
            const Icon = REPORT_ICON_MAP[r.icon] || FileSpreadsheet;
            const isSel = selected.includes(r.key);
            return (
              <button
                type="button"
                key={r.key}
                className={`${classes.reportItem} ${isSel ? classes.reportItemActive : ""}`}
                onClick={() => toggle(r.key)}
              >
                <span className={classes.reportIcon}>
                  <Icon size={16} strokeWidth={1.75} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className={classes.reportName}>{r.title}</div>
                  <div className={classes.reportDesc}>{r.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className={classes.exportFoot}>
        <button type="button" className={classes.ghostBtn} onClick={onClose}>Cancelar</button>
        <button type="button" className={classes.primaryBtn} onClick={handleConfirm} disabled={!selected.length}>
          <Download size={14} />
          Exportar {selected.length ? `(${selected.length})` : ""}
        </button>
      </div>
    </Dialog>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────
const MultiUnidades = () => {
  const classes = useStyles();
  const theme = useTheme();
  const colors = getThemeTokens(theme);

  // View state
  // Suporta deep-link via query string ?tab=charts|sms|overview (preserva
  // bookmarks antigos /dashboard-geral, agora redirecionados para
  // /multi-unidades?tab=charts). Valores invalidos caem em overview.
  const initialView = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get("tab");
      if (q === "sms") return "sms";
      if (q === "overview" || q === "charts" || q === "dashboard") return "dashboard";
    } catch (_) { /* SSR / no window */ }
    return "dashboard";
  })();
  const [view, setView] = useState(initialView); // dashboard | sms | unit
  const [selectedUnit, setSelectedUnit] = useState(null);

  // Dados reais das 8 series mensais + 1 diaria pra sub-aba "Graficos
  // Operacionais". Endpoint /dashboard/multi-unidades-charts roda no mesmo
  // resolveGroupOrSingle do dashboard normal — se o user e admin de grupo
  // (Up&help), retorna agregado das 12 contas; senao, escopa pelo JWT.
  const { user } = useContext(AuthContext);
  const groupName = user?.company?.groupName || "";
  const [chartsReal, setChartsReal] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError, setChartsError] = useState(null);

  // Atalho pros tokens visuais usados pelo loading/empty state das charts
  const c = colors;

  // Fetch das 8 series quando entra na sub-aba "Graficos Operacionais".
  // Refetch tambem se groupName mudar (ex.: super-admin trocando de contexto).
  useEffect(() => {
    if (view !== "charts") return;
    let alive = true;
    setChartsLoading(true);
    setChartsError(null);
    const params = groupName ? { groupName } : {};
    api
      .get("/dashboard/multi-unidades-charts", { params })
      .then(({ data }) => {
        if (!alive) return;
        setChartsReal(data);
      })
      .catch(err => {
        if (!alive) return;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Erro ao carregar gráficos.";
        setChartsError(msg);
      })
      .finally(() => {
        if (alive) setChartsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [view, groupName]);

  // Filters
  const [subsidiary, setSubsidiary] = useState("all");
  const [unitFilter, setUnitFilter] = useState("all");
  const [period, setPeriod] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Table state
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Refresh / export
  const [refreshing, setRefreshing] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // ── Data layer ─────────────────────────────────────────────────────
  // Dataset gerado deterministicamente (5 empresas, 30 SCs, 100 agentes,
  // 1k clientes, 10k conversas, 50k mensagens). Métricas computadas
  // dinamicamente via metrics.js. Substituir por fetch ao backend
  // muda só useMultiUnidadesData — UI continua igual.
  const filtersForHook = useMemo(() => ({
    subsidiary,
    smartCenterId: unitFilter,
    period,
    status: statusFilter,
    search,
  }), [subsidiary, unitFilter, period, statusFilter, search]);

  const {
    kpis: allKpis,
    performance: allUnits,
    sms: smsData,
    optionLists,
    getCasesForUnit,
    refresh: refreshDataset,
    datasetMeta,
    dataSource,
  } = useMultiUnidadesData(filtersForHook);

  const [lastUpdate, setLastUpdate] = useState(() =>
    datasetMeta.now.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" })
  );

  const unitOptions = optionLists.smartCenterOptions;
  const subsidiariesList = optionLists.subsidiaries;

  // Ordenação local (tabela já vem filtrada pelo hook)
  const filteredUnits = useMemo(() => {
    const f = [...allUnits];
    const dir = sortDir === "asc" ? 1 : -1;
    f.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av || "").localeCompare(String(bv || ""), "pt-BR") * dir;
    });
    return f;
  }, [allUnits, sortBy, sortDir]);

  const pagedUnits = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return filteredUnits.slice(startIdx, startIdx + pageSize);
  }, [filteredUnits, page, pageSize]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      refreshDataset();
      setRefreshing(false);
      setLastUpdate(new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" }));
      setSnackbar({ open: true, message: "Dados atualizados." });
    }, 700);
  };

  const handleExport = ({ period: p, reports }) => {
    setExportOpen(false);
    setSnackbar({
      open: true,
      message: `Exportando ${reports.length} relatório(s) referentes ao período "${p}"… (mock)`,
    });
  };

  // ── Render Unit Detail ──────────────────────────────────────────────
  const renderUnitDetail = () => {
    const cases = getCasesForUnit(selectedUnit.id);
    return (
      <>
        <button type="button" className={classes.backButton} onClick={() => setView("dashboard")}>
          <ArrowLeft size={14} />
          Voltar para Multi unidades
        </button>

        <div className={classes.detailHeader}>
          <div className={classes.detailHeaderLeft}>
            <div className={classes.detailHeaderName}>
              <span className={classes.titlePillIcon} style={{ width: 30, height: 30, borderRadius: 8 }}>
                <Building size={15} strokeWidth={2} />
              </span>
              {selectedUnit.name}
              <span className={classes.ascPill}>ASC {selectedUnit.ascCode}</span>
            </div>
            <div className={classes.detailHeaderSub}>
              Casos em aberto e métricas detalhadas desta unidade
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className={classes.headerMeta}>
              <Clock size={11} /> Última atualização: {lastUpdate}
            </span>
            <Tooltip title="Atualizar" arrow>
              <IconButton onClick={handleRefresh} className={classes.iconBtn}>
                <RefreshCw size={14} className={refreshing ? classes.spin : ""} />
              </IconButton>
            </Tooltip>
            <button type="button" className={classes.ghostBtn} onClick={() => setExportOpen(true)}>
              <Download size={14} />
              Exportar
            </button>
          </div>
        </div>

        <div className={classes.miniGrid}>
          <div className={classes.miniCard}>
            <div className={classes.miniLabel}>Casos em aberto</div>
            <div className={classes.miniValue}>{selectedUnit.totalOpen}</div>
          </div>
          <div className={classes.miniCard}>
            <div className={classes.miniLabel}>1ª resp. atendente</div>
            <div className={classes.miniValue}>{selectedUnit.avgFirstReplyAttendant}</div>
          </div>
          <div className={classes.miniCard}>
            <div className={classes.miniLabel}>1ª resp. cliente</div>
            <div className={classes.miniValue}>{selectedUnit.avgFirstReplyClient}</div>
          </div>
          <div className={classes.miniCard}>
            <div className={classes.miniLabel}>Atendimentos hoje</div>
            <div className={classes.miniValue}>{fmtNumberPtBR(selectedUnit.totalToday)}</div>
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <div className={classes.sectionTitle}>
              <FolderOpen size={15} strokeWidth={1.75} />
              Casos em aberto · {selectedUnit.name}
              <span className={classes.countPill}>{cases.length} caso(s)</span>
            </div>
            <div className={classes.sectionTools}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Buscar cliente, agente ou conversa..."
                className={classes.searchField}
                InputProps={{
                  startAdornment: <Search size={14} style={{ marginRight: 8, color: colors.textSubtle }} />,
                }}
              />
            </div>
          </div>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Data e hora de início</th>
                  <th className={classes.th}>Tempo sem resposta</th>
                  <th className={classes.th}>Agente responsável</th>
                  <th className={classes.th}>Cliente</th>
                  <th className={classes.th}>Prévia da última mensagem</th>
                  <th className={classes.th} style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className={classes.tr}>
                    <td className={`${classes.td} ${classes.tdMuted}`}>{fmtDateTime(c.startedAt)}</td>
                    <td className={classes.td}>
                      <span className={`${classes.timeBadge} ${c.noReplyCritical ? classes.timeBadgeFail : classes.timeBadgeOk}`}>
                        {c.noReplyDuration}
                        {c.noReplyCritical && <AlertCircle size={12} />}
                      </span>
                    </td>
                    <td className={`${classes.td} ${classes.tdName}`}>{c.agent}</td>
                    <td className={`${classes.td} ${classes.tdName}`}>{c.client}</td>
                    <td className={`${classes.td} ${classes.tdMuted}`} style={{ maxWidth: 380 }}>
                      {c.lastMessage}
                    </td>
                    <td className={classes.td}>
                      <div className={classes.actionsCell}>
                        <Tooltip title="Ver conversa" arrow>
                          <IconButton size="small" className={classes.actionBtn}>
                            <ExternalLink size={14} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={classes.legendRow}>
            <span className={classes.legendNote}>
              <Info size={12} />
              Exibindo {cases.length} caso(s). Tempos em vermelho indicam ruptura de SLA.
            </span>
          </div>
        </div>
      </>
    );
  };

  // ── Render Charts ─────────────────────────────────────────────────
  // Sub-aba "Graficos operacionais": 8 series mensais REAIS (substituem
  // o gerador deterministico de mockData) puxadas de
  // GET /dashboard/multi-unidades-charts (com groupName quando admin de
  // grupo). Abaixo dos 8, embeda o DashboardGeralBody como complemento
  // (heatmap, pies, ranking ART, etc) preservando filtros da shell.
  const fmtMonthLabel = (key) => {
    if (!key) return "";
    const [y, m] = key.split("-");
    const labels = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return `${labels[parseInt(m, 10) - 1]}/${y.slice(2)}`;
  };
  const fmtDayLabel = (key) => {
    if (!key) return "";
    const [, m, d] = key.split("-");
    return `${d}/${m}`;
  };
  const toChartData = (rows, labelKey, valueKey, labelFmt) =>
    (rows || []).map(r => ({ month: labelFmt(r[labelKey]), value: Number(r[valueKey] ?? r.value) || 0 }));

  const renderCharts = () => {
    if (chartsLoading) {
      return (
        <div className={classes.chartsGrid} style={{ minHeight: 220 }}>
          <div style={{ gridColumn: "1 / -1", padding: 40, textAlign: "center", color: c.textMuted }}>
            Carregando gráficos…
          </div>
        </div>
      );
    }
    if (chartsError || !chartsReal) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: c.textMuted }}>
          {chartsError || "Sem dados disponíveis para os gráficos."}
        </div>
      );
    }
    const r = chartsReal;
    const series = {
      openNoReply: toChartData(r.openNoReply, "month", "value", fmtMonthLabel),
      longestNoReply: toChartData(r.longestNoReplyAttendantSec, "month", "value", fmtMonthLabel),
      avgFirstReplyAtt: toChartData(r.avgFirstReplyAttendantSec, "month", "value", fmtMonthLabel),
      longestFirstReply: toChartData(r.longestFirstReplySec, "month", "value", fmtMonthLabel),
      avgFirstReplyClient: toChartData(r.avgFirstReplyClientSec, "month", "value", fmtMonthLabel),
      totalOpen: toChartData(r.totalOpen, "month", "value", fmtMonthLabel),
      waitingClient: toChartData(r.waitingClient, "month", "value", fmtMonthLabel),
      avgInteraction: toChartData(r.avgInteractionSec, "month", "value", fmtMonthLabel),
      totalDaily: toChartData(r.totalDaily, "date", "value", fmtDayLabel)
    };
    const last = (arr) => arr[arr.length - 1]?.value ?? 0;
    return (
      <>
        <div className={classes.chartsGrid}>
          <ChartCard title="Casos em aberto sem resposta" sub="Volume mês a mês" Icon={MessageSquare} data={series.openNoReply} last={fmtNumberPtBR(last(series.openNoReply))} valueFormatter={fmtNumberPtBR} classes={classes} colors={colors} gradientId="g-openNoReply" />
          <ChartCard title="Maior tempo de caso sem resposta" sub="Pior caso registrado (HH:MM:SS)" Icon={Timer} data={series.longestNoReply} last={secToHms(last(series.longestNoReply))} valueFormatter={secToHms} classes={classes} colors={colors} gradientId="g-longestNoReply" />
          <ChartCard title="Tempo médio 1ª resp. atendente" sub="HH:MM:SS" Icon={Users} data={series.avgFirstReplyAtt} last={secToHms(last(series.avgFirstReplyAtt))} valueFormatter={secToHms} classes={classes} colors={colors} gradientId="g-avgAttendant" />
        </div>
        <div style={{ height: 12 }} />
        <div className={classes.chartsGrid}>
          <ChartCard title="Maior tempo de 1ª resposta" sub="Pior caso de 1ª resposta (HH:MM:SS)" Icon={Clock} data={series.longestFirstReply} last={secToHms(last(series.longestFirstReply))} valueFormatter={secToHms} classes={classes} colors={colors} gradientId="g-longestFirst" />
          <ChartCard title="Tempo médio 1ª resp. cliente" sub="HH:MM:SS" Icon={User} data={series.avgFirstReplyClient} last={secToHms(last(series.avgFirstReplyClient))} valueFormatter={secToHms} classes={classes} colors={colors} gradientId="g-avgClient" />
          <ChartCard title="Total de casos em aberto" sub="Volume mês a mês" Icon={Folder} data={series.totalOpen} last={fmtNumberPtBR(last(series.totalOpen))} valueFormatter={fmtNumberPtBR} classes={classes} colors={colors} gradientId="g-totalOpen" />
        </div>
        <div style={{ height: 12 }} />
        <div className={classes.chartsGrid}>
          <ChartCard title="Aguardando resposta do cliente" sub="Volume mês a mês" Icon={Hourglass} data={series.waitingClient} last={fmtNumberPtBR(last(series.waitingClient))} valueFormatter={fmtNumberPtBR} classes={classes} colors={colors} gradientId="g-waiting" />
          <ChartCard title="Tempo médio total de interação" sub="Por cliente (HH:MM:SS)" Icon={Activity} data={series.avgInteraction} last={secToHms(last(series.avgInteraction))} valueFormatter={secToHms} classes={classes} colors={colors} gradientId="g-avgInter" />
          <ChartCard title="Total de atendimentos por dia" sub="Últimos 30 dias" Icon={ChartLine} data={series.totalDaily} last={fmtNumberPtBR(last(series.totalDaily))} valueFormatter={fmtNumberPtBR} classes={classes} colors={colors} gradientId="g-totalDaily" />
        </div>
        {/* NOTA: DashboardGeralBody embedded foi REMOVIDO desta sub-aba em
            05/06/2026. Causava SequelizeConnectionAcquireTimeoutError em
            cascata: 14 endpoints × 12 contas Up&help em paralelo
            (~168 queries simultâneas) saturavam o pool max=8 do Sequelize,
            derrubando outros endpoints da plataforma. Os 8 sparklines
            acima sao o conteudo canonico desta sub-aba — usuarios que
            precisarem das visualizacoes do Dashboard Geral (heatmap,
            pies, ranking ART) acessam via /dashboard normal ou em janela
            dedicada futura. */}
      </>
    );
  };

  // ── Render Overview ──────────────────────────────────────────────────
  const renderOverview = () => (
    <>
      <div className={classes.section}>
        <div className={classes.sectionHead}>
          <div className={classes.sectionTitle}>
            <Building size={15} strokeWidth={1.75} />
            Performance por Smart Center
            <span className={classes.countPill}>{filteredUnits.length} unidade(s)</span>
          </div>
          <div className={classes.sectionTools}>
            <Tooltip title="Filtrar colunas" arrow>
              <IconButton size="small" className={classes.iconBtn} style={{ width: 32, height: 32 }}>
                <Filter size={13} />
              </IconButton>
            </Tooltip>
            <button type="button" className={classes.ghostBtn} onClick={() => setExportOpen(true)}>
              <Download size={13} />
              Exportar
            </button>
          </div>
        </div>

        <PerformanceTable
          rows={pagedUnits}
          classes={classes}
          colors={colors}
          onOpenUnit={(u) => { setSelectedUnit(u); setView("unit"); }}
          page={page}
          pageSize={pageSize}
          total={filteredUnits.length}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />

        <div className={classes.legendRow}>
          <div className={classes.legend}>
            <span className={classes.legendItem}>
              <span className={`${classes.statusDot} ${classes.statusOk}`} /> Dentro do tempo
            </span>
            <span className={classes.legendItem}>
              <span className={`${classes.statusDot} ${classes.statusWarn}`} /> Atenção
            </span>
            <span className={classes.legendItem}>
              <span className={`${classes.statusDot} ${classes.statusFail}`} /> Fora do tempo
            </span>
          </div>
          <span className={classes.legendNote}>
            <Info size={12} />
            Médias calculadas considerando os casos em aberto no momento da consulta.
          </span>
        </div>
      </div>
    </>
  );

  // ── Render shell ────────────────────────────────────────────────────
  return (
    <div className={classes.pageWrap}>
      <div className={classes.page}>
        {view === "unit" && selectedUnit ? (
          renderUnitDetail()
        ) : view === "sms" ? (
          <SmsAlertsView
            classes={classes}
            colors={colors}
            onBack={() => setView("dashboard")}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            lastUpdate={lastUpdate}
            onExport={() => setExportOpen(true)}
            smsData={{
              alertLevels: smsData.alertLevels,
              sentCounters: smsData.counters,
              partners: smsData.partners,
            }}
          />
        ) : (
          <>
            {/* Header */}
            <div className={classes.header}>
              <div className={classes.headerLeft}>
                <div className={classes.titleRow}>
                  <span className={classes.titlePillIcon}>
                    <Building size={18} strokeWidth={2} />
                  </span>
                  <div className={classes.titleStack}>
                    <div className={classes.title} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      Multi unidades
                      {dataSource && (
                        <Tooltip
                          arrow
                          title={
                            dataSource.usesRealCompanies
                              ? `Smart Centers vêm de /companies/list (${dataSource.realCompaniesCount} empresas reais). Métricas de operação (tempos, contagens) são SIMULADAS porque o backend ainda não tem endpoint de tracking — substituir mocks por fetch real quando estiver pronto.`
                              : "Companies reais não disponíveis (sem permissão ou erro). Usando dataset 100% sintético."
                          }
                        >
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "3px 9px", borderRadius: 999,
                            fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            background: dataSource.usesRealCompanies ? colors.warningSoft : colors.dangerSoft,
                            color: dataSource.usesRealCompanies ? colors.warning : colors.danger,
                            border: `1px solid ${dataSource.usesRealCompanies ? colors.warningSoft : colors.dangerSoft}`,
                          }}>
                            <Info size={10} strokeWidth={2.5} />
                            {dataSource.usesRealCompanies
                              ? `Dev · ${dataSource.realCompaniesCount} empresas reais · operação simulada`
                              : "Dev · dataset sintético"}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <div className={classes.subtitle}>
                      Visão geral de produtividade e atendimento por unidade
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.headerActions}>
                <span className={classes.headerMeta}>
                  <Clock size={11} />
                  Última atualização: {lastUpdate}
                </span>
                <div className={classes.tabBar}>
                  <button
                    type="button"
                    className={`${classes.tabBtn} ${(view === "overview" || view === "charts" || view === "dashboard") ? classes.tabBtnActive : ""}`}
                    onClick={() => setView("dashboard")}
                  >
                    <TableIcon size={13} />
                    Dashboard
                  </button>
                  <button
                    type="button"
                    className={`${classes.tabBtn} ${view === "sms" ? classes.tabBtnActive : ""}`}
                    onClick={() => setView("sms")}
                  >
                    <BellRing size={13} />
                    SMS Alerts
                  </button>
                </div>
                <Tooltip title="Atualizar" arrow>
                  <IconButton onClick={handleRefresh} className={classes.iconBtn}>
                    <RefreshCw size={14} className={refreshing ? classes.spin : ""} />
                  </IconButton>
                </Tooltip>
                <button type="button" className={classes.primaryBtn} onClick={() => setExportOpen(true)}>
                  <Download size={14} />
                  Exportar
                </button>
              </div>
            </div>

            {/* Filters — somente em overview. Em "charts" o DashboardGeralBody
                tem seu proprio filtro de periodo (24h/7d/30d/custom). */}
            {view === "overview" && (
              <div className={classes.filtersRow}>
                <Filter size={14} style={{ color: colors.textMuted }} />
                <ModernSelect
                  label="Subsidiária"
                  value={subsidiary} onChange={(e) => setSubsidiary(e.target.value)}
                  className={classes.filterField}
                  formControlProps={{ size: "small", style: { minWidth: 170 } }}
                >
                  {subsidiariesList.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </ModernSelect>
                <ModernSelect
                  label="Smart Center"
                  value={unitFilter} onChange={(e) => { setUnitFilter(e.target.value); setPage(1); }}
                  className={classes.filterField}
                  formControlProps={{ size: "small", style: { minWidth: 200 } }}
                >
                  {unitOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </ModernSelect>
                <ModernSelect
                  label="Período"
                  value={period} onChange={(e) => setPeriod(e.target.value)}
                  className={classes.filterField}
                  formControlProps={{ size: "small", style: { minWidth: 170 } }}
                >
                  {periodOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </ModernSelect>
                <ModernSelect
                  label="Status"
                  value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className={classes.filterField}
                  formControlProps={{ size: "small", style: { minWidth: 160 } }}
                >
                  {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </ModernSelect>
                <div style={{ flex: 1, minWidth: 0 }} />
                <TextField
                  variant="outlined" size="small" placeholder="Buscar Smart Center..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className={classes.searchField}
                  InputProps={{ startAdornment: <Search size={14} style={{ marginRight: 8, color: colors.textSubtle }} /> }}
                />
              </div>
            )}

            {/* KPIs — somente em overview. Em "charts" o DashboardGeralBody
                renderiza seus proprios metric cards (Atendimentos, ART, etc). */}
            {view === "overview" && (
              <div className={classes.kpiGrid}>
                {allKpis.map((k) => <KpiCard key={k.key} kpi={k} colors={colors} classes={classes} />)}
              </div>
            )}

            {view === "overview" && renderOverview()}
            {/* renderCharts() legado preservado mas nao chamado.
                A sub-aba "Dashboard" agora renderiza MultiUnidadesV2
                (visual SAAS premium com 1 endpoint agregador). */}
            {(view === "dashboard" || view === "charts") && (
              <MultiUnidadesV2 groupName={user?.company?.groupName || ""} />
            )}
          </>
        )}

        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          classes={classes}
          colors={colors}
          onConfirm={handleExport}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
        />
      </div>
    </div>
  );
};

export default MultiUnidades;
